/* ### Array related functions ### */

/**
 * Array.find but works with an async predicate
 */
export const findAsync = async (arr, predicate) => {
  let check = false;
  let result = null;
  for (let i = 0; i < arr.length; i += 1) {
    check = await predicate(arr[i]); // eslint-disable-line no-await-in-loop
    if (check) {
      result = arr[i];
      break;
    }
  }
  return result;
};

/* ### Object related functions ### */

/**
 * Function to shallow merge one or more objects
 *
 * @param  {...Object} objs
 * @returns object with all values merged
 */
export const mergeShallow = (...objs) => Object.assign({}, ...objs);

/**
 * If the default parameter is `undefined` it will be seen as no value and an
 * error will be thrown if it doesn't exist
 *
 * @throws {TypeError} if one of the keys does not exist within the object and a
 *                     default value is not specified
 * @param {Object} obj - object to pick keys from
 * @param {Array<string>} keys - keys to whitelist
 * @param {any} [def] - optional, value to return if key does not exist
 * @returns {Object} - obj with only `keys` in it
 */
export const pick = (obj, keys, def = undefined) => {
  const returnObj = {};
  keys.forEach((key) => {
    if (obj[key] !== undefined) {
      returnObj[key] = obj[key];
    } else if (typeof def !== "undefined") {
      returnObj[key] = def;
    }
  });
  return returnObj;
};

/**
 * Function to remove keys from object
 *
 */
export const removeKeys = (obj, ...keysToRemove) => {
  return Object.keys(obj)
    .filter((key) => !keysToRemove.includes(key))
    .reduce((newObj, key) => {
      const modifiedObj = { ...newObj };
      modifiedObj[key] = obj[key];
      return modifiedObj;
    }, {});
};

/**
 * Detects if `obj` is `{}`
 *
 * @param {Object} obj
 */
export const isEmptyObject = (obj) =>
  Object.entries(obj).length === 0 && obj.constructor === Object;

/**
 * Returns true if item is null or undefined
 *
 * @param {any} item
 */
export const isNil = (item) => item === undefined || item === null;

/**
 * Returns true if item is null or undefined or an empty string
 *
 * @param {any} item
 */
export const isEmpty = (item) => isNil(item) || item === "";

/**
 * This function will retrieve a value from an object using the path specified
 *
 * Path items will go through `parseInt(...)` and if that shows them to be a number
 * it will use that parsed number (for arrays and array-like objects)
 *
 * @example
 *    const obj = {
 *      hello: { world: 'test' }
 *      arr: [{ test: 'array object item'}]
 *    }
 *    getByPath('hello.world', obj) // => 'test'
 *    getByPath('arr.0.test', obj) // => 'array object item'
 * @param {string} path
 * @param {any} item
 */
export const getByPath = (path, item) => {
  if (item == null) {
    return undefined;
  }
  const pathItems = path.split(".");
  const isValue = pathItems.length === 1;
  // see if it is a number or string
  const parseAttempt = parseInt(pathItems[0], 10);
  // eslint-disable-next-line no-restricted-globals
  const isInt = !isNaN(parseAttempt);
  const key = isInt ? parseAttempt : pathItems[0];
  if (isValue) {
    return item[key];
  }
  if (item[key] === undefined) {
    return undefined;
  }
  const subPath = pathItems.splice(1).join(".");
  return getByPath(subPath, item[key]);
};

/* ### Function related functions ### */

const fnReducer = (curr, fn) => fn(curr);

/**
 * This function will take two or more functions and create a function that will 'pipe'
 * a value through those functions such as the following:
 *
 * ```pipe(a, b, c)(val)```
 *
 * is equal to
 *
 * ```c(b(a(val)))```
 *
 * Hopefully this function will be made redundant when the pipe syntax becomes available
 *
 * @param  {...Function} fns functions to pipe
 * @returns a single function which will pipe the value though the functions
 */
export const pipe =
  (...fns) =>
  (x) =>
    fns.reduce((v, f) => f(v), x);

/**
 * This function will take two or more functions and create a function that will 'compose'
 * a value through those functions such as the following:
 *
 * ```compose(a, b, c)(val)```
 *
 * which is equivalent to
 *
 * ```a(b(c(val)))```
 *
 * @param  {...any} fns functions to pipe
 * @returns a single function which will pipe the value though the functions
 */
export const compose =
  (...fns) =>
  (value) =>
    fns.reduceRight(fnReducer, value);

export const noop = () => {};

/* ### Array related functions ### */

/**
 * Returns array of unique items from input
 *
 * @param {Array} arr
 */
export const uniq = (arr) => arr.filter((v, i, a) => a.indexOf(v) === i);

/**
 * Returns array of arr1 items which don't exist in arr2
 *
 * @param {Array} arr1
 * @param {Array} arr2
 */
export const diff = (arr1, arr2) => arr1.filter((x) => !arr2.includes(x));

/**
 * Returns the symmetric difference (exclusive-or) of an array of elements
 *
 * @param {Array} arr1
 * @param {Array} arr2
 *
 * @example
 *   arrayXor(["permanent", "temporary"], ["permanent"]) // => ["temporary"]
 *   arrayXor(["permanent", "temporary"], ["contract"]) // => ["permanent, "temporary", "contract"]
 */
export const arrayXor = (arr1, arr2) => {
  const res = diff(arr1, arr2).concat(diff(arr2, arr1));
  return uniq(res);
};

const isDate = (value) => {
  const dateNaN = Number.isNaN(Number(value.valueOf()));
  return value instanceof Date && !dateNaN;
};

/*
 * Takes an array and a method which returns an object (or an array of objects
 * for multiple sorting i.e. by data and then by name) of sortable key values.
 *
 * The idea is to traverse the array once to extract the actual values used for
 * sorting into a temporary array, sort the temporary array, and then traverse
 * the temporary array to achieve the right order.
 *
 * Note: passing a method through opposed to an array allows for configration
 * against negative values (ascending and decending);
 *
 * @param {Array} arr
 * @param {String} || {Array} keys
 *
 * @example (descending)
 *   sortBy(array, 'key')
 *   sortBy(array, ['key', 'key1'])
 *
 * @example (descending)
 *   sortBy(array, '-key')
 *   sortBy(array, ['-key', '-key1'])
 *
 */
export const sortBy = (arr, keys) => {
  let func;

  if (!arr) {
    throw new Error("A valid array is required to sort");
  }

  if (!arr.length) {
    return arr;
  }

  const getComparableValue = (data, key) => {
    const isNegative = key.includes("-");

    const value = isNegative ? data[key.substr(1, key.length)] : data[key];

    const asDate = new Date(value);
    if (isDate(asDate)) {
      return isNegative ? -asDate : asDate;
    }
    if (typeof value === "string") {
      return isNegative ? -value.charCodeAt(0) : value.charCodeAt(0);
    }
    return isNegative ? -value : value;
  };

  if (typeof keys === "string") {
    func = (data) => getComparableValue(data, keys);
  }

  if (Array.isArray(keys)) {
    func = (data) => keys.map((str) => getComparableValue(data, str));
  }

  if (func === undefined) {
    throw new Error(
      `Params are required as either a string representing an object key 
      or an array of strings representing object keys`
    );
  }

  const comparableArr = arr.map((o) => [].concat(func(o), o));

  comparableArr.sort((a, b) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
    }
    return 0;
  });

  return comparableArr.map((o) => o[o.length - 1]);
};

export const getNavigatorInfo = () => {
  const { userAgent, cookieEnabled, onLine, vendor, language } =
    navigator || {};

  return {
    userAgent,
    cookieEnabled,
    onLine,
    vendor,
    language,
  };
};

if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, "toBlob", {
    value(callback, type, quality) {
      const binStr = atob(this.toDataURL(type, quality).split(",")[1]);
      const len = binStr.length;
      const arr = new Uint8Array(len);

      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < len; i++) {
        arr[i] = binStr.charCodeAt(i);
      }

      callback(new Blob([arr], { type: type || "image/png" }));
    },
  });
}

const canvasToJpegBlob = (canvas) =>
  // eslint-disable-next-line consistent-return
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          return resolve(blob);
        },
        "image/jpeg",
        70
      );
    } catch (err) {
      return reject(err);
    }
  });

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = function onerror(err) {
      return reject(err);
    };
    reader.onload = function onload() {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(",")[1];
      return resolve(base64);
    };
    reader.readAsDataURL(blob);
  });

export const canvasToBase64 = (callback) => (canvas) =>
  canvasToJpegBlob(canvas)
    .then((blob) => blobToBase64(blob))
    .then((base64Str) => callback(base64Str));
